import React, { useEffect, useMemo } from 'react';
import { Contract, ContractType, DashboardMarketingCards } from 'cpes-common';
import { useGetContractBasedApiData } from '@cp-shared-19/frontend-integration';
import { OfflinePaymentDataSelector } from 'components/early-settlement/total-early-settlement/total-early-settlment-offline-payment/OfflinePaymentDataSelector';
import { fetchOfflinePayment } from 'components/early-settlement/total-early-settlement/total-early-settlment-offline-payment/OfflinePaymentSlice';
import { TotalEarlySettlementDataSelector } from 'components/early-settlement/total-early-settlement/TotalEarlySettlementDataSelector';
import { fetchTotalEarlySettlement } from 'components/early-settlement/total-early-settlement/TotalEarlySettlementSlice';
import { withLoadingAndNoConnectionHandler } from 'components/integration-wrapper';
import { usePersonalDetails } from 'components/my-profile/usePersonalDetails';
import { DashboardMarketingCardUi } from './ui';
import { getMostRecentSeatContract } from './utils';
import { useAdditionalProducts } from './additional-products/useAdditionalProducts';
import { useVehicalDetails } from './vehical-details/useVehicalDetails';
import { flatMap } from 'lodash';
import { useTranslation } from 'react-i18next';

const DashboardMarketingCardUiWithHandlers = withLoadingAndNoConnectionHandler(DashboardMarketingCardUi);

type DashboardMarketingCardProps = {
    contracts?: Contract[];
    setHasMarketingCard: (shouldRender: boolean) => void;
    setHasMarketingCardError?: (hasError: boolean) => void;
};

export const DashboardMarketingCard: React.FC<DashboardMarketingCardProps> = ({
    contracts,
    setHasMarketingCard,
    setHasMarketingCardError,
}) => {
    const additionalProducts = useAdditionalProducts(contracts);

    const nonOpertaingLeaseContarctNumbers = contracts
        ?.filter(item => item.contractType !== ContractType.OPERATING_LEASE)
        ?.map(i => i.contractNumber);
    const vehicalDetails = useVehicalDetails(contracts);
    const filteredVehicalDetails = Object.fromEntries(
        Object.entries(vehicalDetails).filter(([key]) => nonOpertaingLeaseContarctNumbers?.includes(key)),
    );

    const vwCommercialVehicles = ['VNLC', 'SMLC', 'COLC'];

    const isUserAlreadyBought = useMemo(
        () =>
            flatMap(additionalProducts, entry => entry.data).some(
                additionalProduct => additionalProduct?.type === 'LONGDRIVE SERVICE',
            ),
        [additionalProducts],
    );

    const isUserAlreadyBoughtMotorCarInsurance = useMemo(
        () =>
            flatMap(additionalProducts, entry => entry.data).some(
                additionalProduct => additionalProduct?.type === 'SEGURO DE AUTO FINANCIADO',
            ),
        [additionalProducts],
    );

    const { t } = useTranslation('cards');
    const dashboardMarketingCards: DashboardMarketingCards = [].concat(t('cards', { returnObjects: true }));

    const contractsCarTypes = useMemo(
        () => flatMap(filteredVehicalDetails, entry => entry.data).map(vehicleDetail => vehicleDetail?.carType),
        [filteredVehicalDetails],
    );

    const includesVWCommercial = contractsCarTypes.some(carType => vwCommercialVehicles?.includes(carType as string));
    const doeasNotIncludesVWCommercial = contractsCarTypes.some(
        carType => !vwCommercialVehicles?.includes(carType as string),
    );

    const contractsBrands = useMemo(
        () => flatMap(filteredVehicalDetails, entry => entry.data).map(vehicleDetail => vehicleDetail?.brandHeader),
        [filteredVehicalDetails],
    );

    const filteredDashboardMarketingCardsbyBrand = dashboardMarketingCards
        ?.filter(card => {
            return contractsBrands.includes(card.brand);
        })
        .filter(card => {
            if (includesVWCommercial && doeasNotIncludesVWCommercial) return true;
            if (includesVWCommercial) return card.isCommercial;
            return !card.isCommercial;
        });

    const mostRecentSeatContract = getMostRecentSeatContract(contracts);

    const {
        data: personalDetails,
        isLoading: isLoadingProfile,
        loadingError: loadingErrorProfile,
    } = usePersonalDetails();

    const {
        data: totalEarlySettelment,
        isLoading: isLoadingTotalEarlySettelment,
        loadingError: loadingTotalEarlySettelment,
    } = useGetContractBasedApiData(
        mostRecentSeatContract?.contractNumber || '',
        fetchTotalEarlySettlement,
        TotalEarlySettlementDataSelector,
        mostRecentSeatContract?._links?.totalEarlySettlement,
    );

    const {
        data: offlinePayment,
        isLoading: isLoadingOfflinePayment,
        loadingError: loadingOfflinePayment,
    } = useGetContractBasedApiData(
        mostRecentSeatContract?.contractNumber || '',
        fetchOfflinePayment,
        OfflinePaymentDataSelector,
        totalEarlySettelment?._links?.offlinePayment,
    );

    const hasError = !!loadingErrorProfile || !!loadingTotalEarlySettelment || !!loadingOfflinePayment;

    useEffect(() => {
        setHasMarketingCardError?.(hasError);
    }, [hasError, setHasMarketingCardError]);

    if (hasError) return null;

    return (
        <DashboardMarketingCardUiWithHandlers
            isLoading={isLoadingProfile || isLoadingTotalEarlySettelment || isLoadingOfflinePayment}
            hasError={false}
            dashboardMarketingCard={filteredDashboardMarketingCardsbyBrand}
            personalDetails={personalDetails}
            mostRecentSeatContract={mostRecentSeatContract}
            offlinePayment={offlinePayment}
            showLongDriveMarketingCard={!isUserAlreadyBought}
            showMototrCarInsuranceCard={!isUserAlreadyBoughtMotorCarInsurance}
            setHasMarketingCard={setHasMarketingCard}
        />
    );
};
